import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useT } from '../i18n';
import { CATEGORIES, CAT_STYLE as CAT_STYLE_BASE, getCategoriesForGender } from '../categories';

// Drivers page uses slightly higher-opacity variants
const CATEGORY_STYLE = Object.fromEntries(
  Object.entries(CAT_STYLE_BASE).map(([k, v]) => [k, {
    bg:     v.bg.replace('0.10)', '0.15)'),
    color:  v.color,
    border: v.border.replace('0.3)', '0.4)'),
    dot:    v.color,
  }])
);

function Drivers({ token, role, isCoAdmin = false, driverId, teamPublicId }) {
  const isAdmin = role === 'admin';
  const isAdminOrCoAdmin = isAdmin || isCoAdmin;
  const navigate = useNavigate();
  const t = useT();

  const [drivers, setDrivers]     = useState([]);
  const [ownerRow, setOwnerRow]   = useState(null); // owner always pinned at top
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterCat, setFilterCat]     = useState('ALL');
  const [filterGender, setFilterGender] = useState('ALL');
  const [search, setSearch]             = useState('');
  const [formData, setFormData]         = useState({ name: '', license_number: '', uci_id: '', phone: '', dob: '', emergency_phone: '', address: '', points: 0, category: 'Elite', gender: '' });
  const [accountData, setAccountData] = useState({ account_email: '', account_password: '' });
  const [message, setMessage]     = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [driverSearch, setDriverSearch] = useState('');
  const [driverSearchLoading, setDriverSearchLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(null); // driver_account_id being invited
  const [joinRequests, setJoinRequests] = useState([]);
  const [jrLoading, setJrLoading] = useState(null); // id being processed
  const [genderOpen, setGenderOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);

  // Invite links (admin only)
  const [showInviteLinks, setShowInviteLinks] = useState(false);
  const [inviteLinks, setInviteLinks]         = useState([]);
  const [inviteLinkLoading, setInviteLinkLoading] = useState(false);
  const [newLinkDays, setNewLinkDays]   = useState(7);
  const [newLinkMax, setNewLinkMax]     = useState('');
  const [newLinkLabel, setNewLinkLabel] = useState('');
  const [copiedLinkId, setCopiedLinkId] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadDrivers(); if (isAdminOrCoAdmin) loadJoinRequests(); }, []);

  const loadDrivers = async () => {
    try {
      const res = await axios.get('/api/drivers', { headers: { Authorization: `Bearer ${token}` } });
      const all = res.data || [];
      // First row may be the synthetic owner row injected by the backend
      const first = all[0];
      if (first?._isOwner) {
        setOwnerRow(first);
        setDrivers(all.slice(1));
      } else {
        setOwnerRow(null);
        setDrivers(all);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadJoinRequests = async () => {
    try {
      const res = await axios.get('/api/drivers/join-requests', { headers: { Authorization: `Bearer ${token}` } });
      setJoinRequests(res.data);
    } catch (err) { console.error(err); }
  };

  const handleJoinRequestAction = async (id, action) => {
    setJrLoading(id);
    try {
      const res = await axios.post(`/api/drivers/join-requests/${id}/${action}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      showMsg(res.data.message);
      loadJoinRequests();
      if (action === 'approve') loadDrivers();
    } catch (err) {
      showMsg('Fehler: ' + (err.response?.data?.error || err.message));
    } finally {
      setJrLoading(null);
    }
  };

  const showMsg = (msg) => { setMessage(msg); setTimeout(() => setMessage(''), 3000); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const payload = { ...formData, email: accountData.account_email || '' };
        await axios.put(`/api/drivers/${editingId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        showMsg(t('drivers_msg_updated'));
      } else {
        const payload = isAdminOrCoAdmin ? { ...formData, email: accountData.account_email || '', ...accountData } : formData;
        await axios.post('/api/drivers', payload, { headers: { Authorization: `Bearer ${token}` } });
        showMsg(t('drivers_msg_added'));
      }
      resetForm(); loadDrivers();
    } catch (err) { showMsg('Fehler: ' + (err.response?.data?.error || err.message)); }
  };

  const searchAvailableDrivers = async (q) => {
    setDriverSearchLoading(true);
    try {
      const res = await axios.get(`/api/drivers/available?q=${encodeURIComponent(q)}`, { headers: { Authorization: `Bearer ${token}` } });
      setAvailableDrivers(res.data);
    } catch (err) {
      showMsg('Fehler: ' + (err.response?.data?.error || err.message));
    } finally {
      setDriverSearchLoading(false);
    }
  };

  const handleOpenInvite = () => {
    setShowInvite(v => !v);
    resetForm();
    if (!showInvite) searchAvailableDrivers('');
  };

  // ── Invite Links (admin only) ─────────────────────────────────────────────
  const loadInviteLinks = async () => {
    try {
      const res = await axios.get('/api/admin/invite-links', { headers: { Authorization: `Bearer ${token}` } });
      setInviteLinks(res.data);
    } catch (err) { console.error('[invite-links]', err.message); }
  };

  const handleOpenInviteLinks = () => {
    setShowInviteLinks(v => !v);
    if (!showInviteLinks) loadInviteLinks();
  };

  const handleCreateLink = async () => {
    setInviteLinkLoading(true);
    try {
      await axios.post('/api/admin/invite-links', {
        label: newLinkLabel.trim() || null,
        expiresInDays: newLinkDays,
        maxUses: newLinkMax ? parseInt(newLinkMax, 10) : null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setNewLinkLabel('');
      setNewLinkDays(7);
      setNewLinkMax('');
      loadInviteLinks();
    } catch (err) {
      showMsg('Fehler: ' + (err.response?.data?.error || err.message));
    } finally {
      setInviteLinkLoading(false);
    }
  };

  const handleRevokeLink = async (id) => {
    if (!window.confirm('Link wirklich widerrufen?')) return;
    try {
      await axios.delete(`/api/admin/invite-links/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      loadInviteLinks();
    } catch (err) {
      showMsg('Fehler: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCopyLink = (token_val, id) => {
    const url = `${window.location.origin}/join/${token_val}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedLinkId(id);
      setTimeout(() => setCopiedLinkId(null), 2000);
    }).catch(() => {});
  };

  const handleDriverSearchChange = (e) => {
    const q = e.target.value;
    setDriverSearch(q);
    searchAvailableDrivers(q);
  };

  const handleInvite = async (driverAccountId) => {
    setInviteLoading(driverAccountId);
    try {
      const res = await axios.post('/api/drivers/invites', { driver_account_id: driverAccountId }, { headers: { Authorization: `Bearer ${token}` } });
      showMsg('✓ ' + res.data.message);
      // Refresh list so invite_status updates
      searchAvailableDrivers(driverSearch);
    } catch (err) {
      showMsg('Fehler: ' + (err.response?.data?.error || err.message));
    } finally {
      setInviteLoading(null);
    }
  };

  const resetForm = () => {
    setShowForm(false); setEditingId(null);
    setFormData({ name: '', license_number: '', uci_id: '', phone: '', dob: '', emergency_phone: '', address: '', points: 0, category: 'Elite', gender: '' });
    setAccountData({ account_email: '', account_password: '' });
  };

  // If owner is racing, merge into the sorted list. Otherwise keep separate (always-top logic).
  const racingOwnerInList = ownerRow?.is_racing ? ownerRow : null;

  const visibleDrivers = drivers
    .filter((d) => {
      const catMatch = filterCat === 'ALL' || (d.category || 'Elite').toUpperCase() === filterCat;
      const genderMatch = filterGender === 'ALL' || (d.gender || '') === filterGender;
      const searchMatch = !search || d.name.toLowerCase().includes(search.toLowerCase());
      return catMatch && genderMatch && searchMatch;
    })
    .concat((racingOwnerInList && (() => {
      // Apply same filters to owner when racing
      const catMatch = filterCat === 'ALL' || (racingOwnerInList.category || 'Elite').toUpperCase() === filterCat;
      const genderMatch = filterGender === 'ALL' || (racingOwnerInList.gender || '') === filterGender;
      const searchMatch = !search || (racingOwnerInList.name || '').toLowerCase().includes(search.toLowerCase());
      return catMatch && genderMatch && searchMatch ? [racingOwnerInList] : [];
    })()) || [])
    .sort((a, b) => {
      // Ranked drivers first (ascending by rank), unranked at end sorted by points desc
      const aRang = a.radnet_rang ?? null;
      const bRang = b.radnet_rang ?? null;
      if (aRang !== null && bRang !== null) return aRang - bRang;
      if (aRang !== null) return -1;
      if (bRang !== null) return 1;
      const ptsDiff = (b.points ?? 0) - (a.points ?? 0);
      if (ptsDiff !== 0) return ptsDiff;
      return (a.name || '').localeCompare(b.name || '');
    });

  // Owner visibility (non-racing only — racing owner is handled via visibleDrivers):
  // Show only when filter is ALL and search matches
  const ownerVisible = ownerRow && !ownerRow.is_racing && (() => {
    const searchMatch = !search || (ownerRow.name || '').toLowerCase().includes(search.toLowerCase());
    return searchMatch && filterCat === 'ALL';
  })();

  const counts = {};
  CATEGORIES.forEach((c) => { counts[c] = drivers.filter((d) => (d.category || 'Elite') === c).length; });

  return (
    <div>
      {/* ── PAGE HEADER ── */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6 }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.2em', color: 'var(--primary)', textTransform: 'uppercase' }}>
            {t('drivers_title')}
          </div>
          {teamPublicId && (
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, color: 'var(--primary)', opacity: 0.55, letterSpacing: '0.12em' }}>
              {teamPublicId}
            </div>
          )}
        </div>
        <div className="drv-page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <h1 style={{ margin: 0, fontFamily: "var(--font-display)", fontWeight: 800, fontStyle: 'italic', fontSize: 'clamp(1.4rem, 4vw, 2.2rem)', letterSpacing: '0.04em', color: 'var(--on-surface)' }}>
            {t('nav_drivers').toUpperCase()}
          </h1>
          <div className="drv-header-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, fontWeight: 700, color: 'var(--primary)', letterSpacing: '-0.02em', lineHeight: 1 }}>
              {String(drivers.length).padStart(2, '0')}
            </div>
            {isAdminOrCoAdmin && (
              <div className="drv-header-admin-btns" style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleOpenInvite}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 14px', background: 'transparent', color: 'var(--brand-secondary)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', border: '1px solid rgba(var(--brand-secondary-rgb),0.4)', cursor: 'pointer', transition: 'all 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(var(--brand-secondary-rgb),0.08)'; e.currentTarget.style.borderColor = 'rgba(var(--brand-secondary-rgb),0.7)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(var(--brand-secondary-rgb),0.4)'; }}
                >
                  ✉ {t('drivers_invite').toUpperCase()}
                </button>
                {isAdmin && (
                  <button
                    onClick={handleOpenInviteLinks}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 14px', background: showInviteLinks ? 'rgba(var(--brand-primary-rgb),0.08)' : 'transparent', color: 'var(--brand-primary)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', border: '1px solid rgba(var(--brand-primary-rgb),0.35)', cursor: 'pointer', transition: 'all 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(var(--brand-primary-rgb),0.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = showInviteLinks ? 'rgba(var(--brand-primary-rgb),0.08)' : 'transparent'; }}
                    title="Einladungslinks generieren"
                  >
                    🔗 LINK
                  </button>
                )}
                <button
                  onClick={() => { resetForm(); setShowForm(true); setShowInvite(false); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', background: 'var(--primary)', color: '#002022', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', border: 'none', cursor: 'pointer', boxShadow: '0 0 14px rgba(var(--brand-primary-rgb),0.35)', transition: 'box-shadow 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = '0 0 22px rgba(var(--brand-primary-rgb),0.55)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = '0 0 14px rgba(var(--brand-primary-rgb),0.35)'}
                >
                  <span style={{ fontSize: 14 }}>＋</span> {t('drivers_form_new').toUpperCase()}
                </button>
              </div>
            )}
            {!isAdminOrCoAdmin && (
              <button onClick={() => navigate('/profile')} style={{ padding: '10px 16px', background: 'transparent', border: '1px solid var(--outline-variant)', color: 'var(--on-surface-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer' }}>
                {t('drivers_my_profile').toUpperCase()}
              </button>
            )}
          </div>
        </div>
      </div>

      {message && (
        <div style={{ padding: '10px 14px', marginBottom: '1rem', background: message.includes('Fehler') ? 'rgba(147,0,10,0.15)' : 'rgba(var(--brand-secondary-rgb),0.1)', border: `1px solid ${message.includes('Fehler') ? 'rgba(255,180,171,0.3)' : 'rgba(var(--brand-secondary-rgb),0.3)'}`, color: message.includes('Fehler') ? 'var(--error)' : 'var(--brand-secondary)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: '0.08em' }}>
          {message}
        </div>
      )}

      {/* ── JOIN REQUESTS ── */}
      {isAdminOrCoAdmin && joinRequests.length > 0 && (
        <div className="drv-invite-panel" style={{ marginBottom: '1.5rem', background: 'rgba(26,28,32,0.6)', border: '1px solid rgba(var(--brand-primary-rgb),0.2)', backdropFilter: 'blur(20px)', padding: '1.25rem 1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--primary)' }}>{t('drivers_join_request').toUpperCase()}</div>
            <span style={{ background: 'var(--primary)', color: 'var(--accent-contrast)', fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, padding: '2px 7px', borderRadius: 10 }}>{joinRequests.length}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {joinRequests.map(jr => (
              <div key={jr.id} className="drv-jr-row" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', background: 'rgba(var(--brand-primary-rgb),0.04)', border: '1px solid rgba(var(--brand-primary-rgb),0.12)', flexWrap: 'wrap' }}>
                {/* Avatar */}
                <div style={{ width: 40, height: 40, borderRadius: 4, background: jr.photo_url ? 'transparent' : 'rgba(var(--brand-primary-rgb),0.1)', border: '1px solid rgba(var(--brand-primary-rgb),0.2)', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {jr.photo_url
                    ? <img src={jr.photo_url} alt={jr.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 14, color: 'var(--primary)' }}>{(jr.name || jr.email || '?')[0].toUpperCase()}</span>
                  }
                </div>
                {/* Info */}
                <div style={{ flex: 1, minWidth: 120 }}>
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14, color: '#e2e2e8', marginBottom: 2 }}>{jr.name || '—'}</div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#849495', letterSpacing: '0.08em' }}>{jr.email} · {jr.category || 'Elite'}</div>
                  {jr.message && <div style={{ fontFamily: "var(--font-display)", fontSize: 12, color: 'rgba(185,202,203,0.7)', marginTop: 4, fontStyle: 'italic' }}>"{jr.message}"</div>}
                </div>
                {/* Actions */}
                <div className="drv-jr-actions" style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button
                    disabled={jrLoading === jr.id}
                    onClick={() => handleJoinRequestAction(jr.id, 'approve')}
                    style={{ padding: '7px 14px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', background: 'rgba(var(--brand-secondary-rgb),0.12)', border: '1px solid rgba(var(--brand-secondary-rgb),0.4)', color: 'var(--brand-secondary)', cursor: 'pointer', opacity: jrLoading === jr.id ? 0.5 : 1 }}>
                    {t('drivers_accept').toUpperCase()}
                  </button>
                  <button
                    disabled={jrLoading === jr.id}
                    onClick={() => handleJoinRequestAction(jr.id, 'reject')}
                    style={{ padding: '7px 14px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', background: 'transparent', border: '1px solid rgba(59,73,75,0.5)', color: '#849495', cursor: 'pointer', opacity: jrLoading === jr.id ? 0.5 : 1 }}>
                    {t('drivers_decline').toUpperCase()}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── INVITE PANEL ── */}
      {showInvite && (
        <div className="drv-invite-panel" style={{ background: 'rgba(26,28,32,0.6)', border: '1px solid rgba(var(--brand-secondary-rgb),0.25)', backdropFilter: 'blur(20px)', padding: '1.25rem 1.5rem', marginBottom: '1.5rem' }}>
          <div className="drv-invite-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--brand-secondary)', marginBottom: 3 }}>{t('drivers_invite').toUpperCase()}</div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 12, color: 'rgba(185,202,203,0.6)' }}>
                {t('drivers_invite_hint')}
              </div>
            </div>
            <button
              type="button"
              className="drv-invite-close"
              onClick={() => { setShowInvite(false); setDriverSearch(''); setAvailableDrivers([]); }}
              style={{ padding: '8px 12px', background: 'transparent', border: '1px solid var(--outline-variant)', color: 'var(--on-surface-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
            >✕</button>
          </div>

          {/* Search input */}
          <div style={{ position: 'relative', marginBottom: 14 }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'rgba(var(--brand-secondary-rgb),0.6)', fontSize: 14 }}>⌕</span>
            <input
              type="text"
              placeholder={t('drivers_invite_placeholder').toUpperCase()}
              value={driverSearch}
              onChange={handleDriverSearchChange}
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px 10px 36px', background: 'rgba(26,28,32,0.8)', border: '1px solid rgba(var(--brand-secondary-rgb),0.3)', color: '#e2e2e8', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', outline: 'none', transition: 'border-color 0.2s' }}
              onFocus={e => e.target.style.borderColor = 'rgba(var(--brand-secondary-rgb),0.7)'}
              onBlur={e => e.target.style.borderColor = 'rgba(var(--brand-secondary-rgb),0.3)'}
            />
          </div>

          {/* Results */}
          {driverSearchLoading ? (
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'rgba(185,202,203,0.4)', letterSpacing: '0.1em', padding: '12px 0' }}>SUCHE...</div>
          ) : availableDrivers.length === 0 ? (
            <div style={{ fontFamily: "var(--font-display)", fontSize: 13, color: 'rgba(185,202,203,0.5)', padding: '16px 0', textAlign: 'center' }}>
              {driverSearch ? t('drivers_invite_no_results') : t('drivers_invite_no_team')}
            </div>
          ) : (
            <div style={{ border: '1px solid rgba(59,73,75,0.35)', borderRadius: 4, overflow: 'hidden', maxHeight: 340, overflowY: 'auto' }}>
              {/* Table header */}
              <div className="drv-invite-table-header" style={{ display: 'grid', gridTemplateColumns: '36px 1fr 80px 80px 110px', alignItems: 'center', gap: 10, padding: '8px 14px', borderBottom: '2px solid rgba(59,73,75,0.35)', background: 'rgba(26,28,32,0.7)', position: 'sticky', top: 0, zIndex: 1 }}>
                <span />
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'rgba(185,202,203,0.5)', textTransform: 'uppercase' }}>{t('drivers_col_rider')}</span>
                <span className="drv-invite-id-col" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'rgba(185,202,203,0.5)', textTransform: 'uppercase' }}>ID</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'rgba(185,202,203,0.5)', textTransform: 'uppercase' }}>{t('drivers_col_class')}</span>
                <span />
              </div>
              {availableDrivers.map((d, i) => {
                const cat = d.category || 'Elite';
                const cs = CATEGORY_STYLE[cat] || CATEGORY_STYLE.Elite;
                const initials = (d.name || d.email || '?')[0].toUpperCase();
                const actionBtn = d.invite_status === 'pending' ? (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 16, color: 'var(--brand-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                    ✓
                  </span>
                ) : (
                  <button
                    disabled={inviteLoading === d.id}
                    onClick={() => handleInvite(d.id)}
                    style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 22, fontWeight: 300, background: inviteLoading === d.id ? 'transparent' : 'rgba(var(--brand-secondary-rgb),0.1)', border: '1px solid rgba(var(--brand-secondary-rgb),0.4)', color: 'var(--brand-secondary)', cursor: inviteLoading === d.id ? 'default' : 'pointer', opacity: inviteLoading === d.id ? 0.5 : 1, borderRadius: 4, transition: 'all 0.15s', flexShrink: 0 }}
                    onMouseEnter={e => { if (inviteLoading !== d.id) e.currentTarget.style.background = 'rgba(var(--brand-secondary-rgb),0.18)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(var(--brand-secondary-rgb),0.1)'; }}
                    title={d.invite_status === 'declined' ? t('drivers_invite_again') : t('drivers_invite_send')}
                  >
                    {inviteLoading === d.id ? '…' : '+'}
                  </button>
                );
                return (
                  <div
                    key={d.id}
                    className="drv-invite-row"
                    style={{
                      display: 'grid', gridTemplateColumns: '36px 1fr 80px 80px 110px',
                      alignItems: 'center', gap: 10, padding: '10px 14px',
                      borderTop: i === 0 ? 'none' : '1px solid rgba(59,73,75,0.2)',
                      background: 'transparent', transition: 'background 0.12s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(var(--brand-secondary-rgb),0.04)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    {/* Col 1: Avatar */}
                    <div style={{ width: 34, height: 34, flexShrink: 0, borderRadius: 3, background: d.photo_url ? 'transparent' : cs.bg, border: `1px solid ${cs.border}`, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {d.photo_url
                        ? <img src={d.photo_url} alt={d.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <span style={{ fontFamily: "var(--font-display)", fontSize: 12, fontWeight: 800, fontStyle: 'italic', color: cs.color }}>{initials}</span>
                      }
                    </div>
                    {/* Col 2: Name */}
                    <span style={{ fontFamily: "var(--font-display)", fontSize: 13, fontWeight: 600, color: '#e2e2e8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {d.name || d.email || '—'}
                    </span>
                    {/* Col 3: Fahrer-ID */}
                    <span className="drv-invite-id-col" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 500, color: 'rgba(185,202,203,0.5)', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                      {d.public_id || '—'}
                    </span>
                    {/* Col 4: Kategorie-Badge */}
                    <span>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', color: cs.color, background: cs.bg, border: `1px solid ${cs.border}`, padding: '3px 7px', borderRadius: 99, whiteSpace: 'nowrap' }}>
                        {cat.toUpperCase()}
                      </span>
                    </span>
                    {/* Col 5: Action */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {actionBtn}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── INVITE LINKS PANEL (admin only) ── */}
      {isAdmin && showInviteLinks && (
        <div style={{ background: 'rgba(26,28,32,0.6)', border: '1px solid rgba(var(--brand-primary-rgb),0.25)', backdropFilter: 'blur(20px)', padding: '1.25rem 1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--brand-primary)', marginBottom: 3 }}>EINLADUNGSLINKS</div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 12, color: 'rgba(185,202,203,0.6)' }}>
                Link generieren &amp; teilen — Fahrer beitreten direkt ohne manuelle Suche.
              </div>
            </div>
            <button onClick={() => setShowInviteLinks(false)} style={{ padding: '8px 12px', background: 'transparent', border: '1px solid rgba(59,73,75,0.4)', color: '#849495', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>✕</button>
          </div>

          {/* Create new link */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, padding: '14px 16px', background: 'rgba(var(--brand-primary-rgb),0.04)', border: '1px solid rgba(var(--brand-primary-rgb),0.12)' }}>
            <input
              type="text"
              placeholder="BEZEICHNUNG (OPTIONAL)"
              value={newLinkLabel}
              onChange={e => setNewLinkLabel(e.target.value)}
              style={{ flex: 2, minWidth: 140, padding: '9px 12px', background: 'rgba(26,28,32,0.8)', border: '1px solid rgba(59,73,75,0.4)', color: '#e2e2e8', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: '0.06em', outline: 'none' }}
            />
            <select
              value={newLinkDays}
              onChange={e => setNewLinkDays(parseInt(e.target.value, 10))}
              style={{ flex: 1, minWidth: 80, padding: '9px 10px', background: 'rgba(26,28,32,0.8)', border: '1px solid rgba(59,73,75,0.4)', color: '#849495', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, outline: 'none' }}
            >
              {[1, 3, 7, 14, 30].map(d => <option key={d} value={d}>{d} TAGE</option>)}
            </select>
            <input
              type="number"
              placeholder="MAX USES"
              value={newLinkMax}
              onChange={e => setNewLinkMax(e.target.value)}
              min={1}
              style={{ flex: 1, minWidth: 80, padding: '9px 12px', background: 'rgba(26,28,32,0.8)', border: '1px solid rgba(59,73,75,0.4)', color: '#849495', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, outline: 'none' }}
            />
            <button
              onClick={handleCreateLink}
              disabled={inviteLinkLoading}
              style={{ padding: '9px 18px', background: 'var(--brand-primary)', color: '#002022', border: 'none', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', cursor: inviteLinkLoading ? 'default' : 'pointer', opacity: inviteLinkLoading ? 0.6 : 1, flexShrink: 0 }}
            >
              {inviteLinkLoading ? '…' : '+ LINK ERSTELLEN'}
            </button>
          </div>

          {/* Existing links */}
          {inviteLinks.length === 0 ? (
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#849495', textAlign: 'center', padding: '16px 0' }}>
              Noch keine Links erstellt.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {inviteLinks.map(link => {
                const expired = new Date(link.expires_at) < new Date();
                const exhausted = link.max_uses !== null && link.use_count >= link.max_uses;
                const invalid = link.revoked || expired || exhausted;
                const statusColor = link.revoked ? '#ff6b6b' : expired ? '#ff9a1f' : exhausted ? '#ff9a1f' : 'var(--brand-secondary)';
                const statusLabel = link.revoked ? 'WIDERRUFEN' : expired ? 'ABGELAUFEN' : exhausted ? 'VOLL' : 'AKTIV';
                const expiryStr = new Date(link.expires_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
                return (
                  <div key={link.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: 8, alignItems: 'center', padding: '10px 14px', background: invalid ? 'rgba(26,28,32,0.3)' : 'rgba(26,28,32,0.7)', border: `1px solid ${invalid ? 'rgba(59,73,75,0.25)' : 'rgba(59,73,75,0.45)'}`, opacity: invalid ? 0.55 : 1 }}>
                    {/* Info */}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#e2e2e8', fontWeight: 700, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {link.label || `Link #${link.id}`}
                      </div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#849495', letterSpacing: '0.04em' }}>
                        <span style={{ color: statusColor, marginRight: 8 }}>{statusLabel}</span>
                        bis {expiryStr}
                        {link.max_uses !== null && ` · ${link.use_count}/${link.max_uses} genutzt`}
                        {link.max_uses === null && link.use_count > 0 && ` · ${link.use_count}× genutzt`}
                      </div>
                    </div>
                    {/* Status badge */}
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, color: statusColor, border: `1px solid ${statusColor}`, padding: '3px 7px', letterSpacing: '0.1em', flexShrink: 0 }}>
                      {statusLabel}
                    </span>
                    {/* Copy URL */}
                    {!invalid && (
                      <button
                        onClick={() => handleCopyLink(link.token, link.id)}
                        style={{ padding: '7px 12px', background: copiedLinkId === link.id ? 'rgba(var(--brand-secondary-rgb),0.15)' : 'transparent', border: `1px solid ${copiedLinkId === link.id ? 'rgba(var(--brand-secondary-rgb),0.4)' : 'rgba(59,73,75,0.5)'}`, color: copiedLinkId === link.id ? 'var(--brand-secondary)' : '#849495', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.08em', flexShrink: 0, transition: 'all 0.15s' }}
                      >
                        {copiedLinkId === link.id ? '✓ KOPIERT' : '📋 URL'}
                      </button>
                    )}
                    {/* Revoke */}
                    {!link.revoked && (
                      <button
                        onClick={() => handleRevokeLink(link.id)}
                        style={{ padding: '7px 10px', background: 'transparent', border: '1px solid rgba(255,107,107,0.3)', color: 'rgba(255,107,107,0.6)', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
                        title="Link widerrufen"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── SEARCH + FILTER ── */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Search */}
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--outline)', fontSize: 14 }}>⌕</span>
          <input
            type="text"
            placeholder="SEARCH ATHLETES..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', padding: '12px 12px 12px 36px', background: 'rgba(26,28,32,0.8)', border: '1px solid rgba(59,73,75,0.4)', color: 'var(--primary)', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
            onFocus={e => e.target.style.borderColor = 'rgba(var(--brand-primary-rgb),0.5)'}
            onBlur={e => e.target.style.borderColor = 'rgba(59,73,75,0.4)'}
          />
        </div>
        {/* Gender + Category filter dropdowns */}
        <div style={{ display: 'flex', gap: 10 }}>

          {/* Gender dropdown */}
          <div style={{ position: 'relative', flex: 1 }}>
            <div
              onClick={() => { setGenderOpen(o => !o); setCatOpen(false); }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'rgba(26,28,32,0.8)', border: '1px solid rgba(59,73,75,0.4)', borderLeft: `3px solid ${filterGender === 'männlich' ? '#7ecfff' : filterGender === 'weiblich' ? '#ff6eb4' : 'rgba(59,73,75,0.4)'}`, cursor: 'pointer', userSelect: 'none' }}
            >
              <div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#849495', letterSpacing: '0.1em', marginBottom: 2 }}>GESCHLECHT</div>
                <div style={{ fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 600, color: filterGender === 'ALL' ? '#b9cacb' : '#ffffff' }}>
                  {filterGender === 'männlich' ? '♂ Männlich' : filterGender === 'weiblich' ? '♀ Weiblich' : 'Alle'}
                </div>
              </div>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#849495', display: 'inline-block', transform: genderOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>
            </div>
            {genderOpen && (
              <>
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, border: '1px solid rgba(var(--brand-primary-rgb),0.18)', borderTop: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.85)', background: 'var(--surface-shell)' }}>
                  {[
                    { value: 'ALL', label: 'Alle', sub: 'GESCHLECHT', accent: 'rgba(var(--brand-primary-rgb),0.5)' },
                    { value: 'männlich', label: '♂ Männlich', sub: 'MÄNNLICH', accent: '#7ecfff' },
                    { value: 'weiblich', label: '♀ Weiblich', sub: 'WEIBLICH', accent: '#ff6eb4' },
                  ].map(({ value, label, sub, accent }) => {
                    const active = filterGender === value;
                    return (
                      <div
                        key={value}
                        onClick={() => { setFilterGender(value); setFilterCat('ALL'); setGenderOpen(false); }}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderLeft: `3px solid ${active ? accent : 'rgba(59,73,75,0.3)'}`, background: active ? 'rgba(0,36,40,0.95)' : '#111318', cursor: 'pointer', borderTop: '1px solid rgba(59,73,75,0.15)' }}
                      >
                        <div>
                          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: active ? accent : '#849495', letterSpacing: '0.1em', marginBottom: 2 }}>{sub}</div>
                          <div style={{ fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 600, color: active ? '#ffffff' : '#b9cacb' }}>{label}</div>
                        </div>
                        {active && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: accent }}>✓</span>}
                      </div>
                    );
                  })}
                </div>
                <div onClick={() => setGenderOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
              </>
            )}
          </div>

          {/* Category dropdown */}
          <div style={{ position: 'relative', flex: 1 }}>
            <div
              onClick={() => { setCatOpen(o => !o); setGenderOpen(false); }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'rgba(26,28,32,0.8)', border: '1px solid rgba(59,73,75,0.4)', borderLeft: `3px solid ${filterCat === 'ALL' ? 'rgba(59,73,75,0.4)' : 'rgba(var(--brand-primary-rgb),0.5)'}`, cursor: 'pointer', userSelect: 'none' }}
            >
              <div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#849495', letterSpacing: '0.1em', marginBottom: 2 }}>RENNKLASSE</div>
                <div style={{ fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 600, color: filterCat === 'ALL' ? '#b9cacb' : '#ffffff' }}>
                  {filterCat === 'ALL' ? t('drivers_all_classes') : filterCat}
                </div>
              </div>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#849495', display: 'inline-block', transform: catOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>
            </div>
            {catOpen && (
              <>
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, border: '1px solid rgba(var(--brand-primary-rgb),0.18)', borderTop: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.85)', background: 'var(--surface-shell)', maxHeight: '60vh', overflowY: 'auto' }}>
                  <div
                    onClick={() => { setFilterCat('ALL'); setCatOpen(false); }}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderLeft: `3px solid ${filterCat === 'ALL' ? 'rgba(var(--brand-primary-rgb),0.5)' : 'rgba(59,73,75,0.3)'}`, background: filterCat === 'ALL' ? 'rgba(0,36,40,0.95)' : '#111318', cursor: 'pointer' }}
                  >
                    <div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: filterCat === 'ALL' ? 'var(--brand-primary)' : '#849495', letterSpacing: '0.1em', marginBottom: 2 }}>FILTER</div>
                      <div style={{ fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 600, color: filterCat === 'ALL' ? '#ffffff' : '#b9cacb' }}>{t('drivers_all_classes')}</div>
                    </div>
                    {filterCat === 'ALL' && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'var(--brand-primary)' }}>✓</span>}
                  </div>
                  {getCategoriesForGender(filterGender === 'ALL' ? null : filterGender).map(c => {
                    const active = filterCat === c.toUpperCase();
                    return (
                      <div
                        key={c}
                        onClick={() => { setFilterCat(c.toUpperCase()); setCatOpen(false); }}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderLeft: `3px solid ${active ? 'rgba(var(--brand-primary-rgb),0.5)' : 'rgba(59,73,75,0.3)'}`, background: active ? 'rgba(0,36,40,0.95)' : '#111318', cursor: 'pointer', borderTop: '1px solid rgba(59,73,75,0.15)' }}
                      >
                        <div>
                          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: active ? 'var(--brand-primary)' : '#849495', letterSpacing: '0.1em', marginBottom: 2 }}>{c.toUpperCase()}</div>
                          <div style={{ fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 600, color: active ? '#ffffff' : '#b9cacb' }}>{c}</div>
                        </div>
                        {active && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'var(--brand-primary)' }}>✓</span>}
                      </div>
                    );
                  })}
                </div>
                <div onClick={() => setCatOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
              </>
            )}
          </div>

        </div>
      </div>

      {/* ── ADD/EDIT FORM ── */}
      {showForm && (
        <div style={{ background: 'rgba(26,28,32,0.6)', border: '1px solid rgba(59,73,75,0.4)', backdropFilter: 'blur(20px)', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--primary)', marginBottom: 4 }}>
            {editingId ? 'EDIT RIDER' : 'NEW RIDER'}
          </div>
          <h3 style={{ fontFamily: "var(--font-display)", fontStyle: 'italic', fontSize: 16, fontWeight: 700, color: 'var(--on-surface)', margin: '0 0 1.25rem' }}>
            {editingId ? t('drivers_form_edit') : t('drivers_form_new_title')}
          </h3>
          <form onSubmit={handleSubmit}>
            {/* Section: Stammdaten */}
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--primary)', textTransform: 'uppercase', marginBottom: 12 }}>
              {t('drivers_form_section_basic').toUpperCase()}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 1.5rem' }}>
              {[
                { label: `${t('drivers_form_name')} *`, key: 'name', type: 'text', required: true },
                { label: t('drivers_form_dob'), key: 'dob', type: 'date' },
                { label: t('drivers_form_phone'), key: 'phone', type: 'tel' },
                { label: t('drivers_form_emergency'), key: 'emergency_phone', type: 'tel' },
                { label: t('drivers_form_address'), key: 'address', type: 'text' },
              ].map(({ label, key, type, required }) => (
                <div key={key} style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--outline)', textTransform: 'uppercase', marginBottom: 6 }}>{label}</label>
                  <input
                    type={type}
                    value={formData[key]}
                    onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                    required={required}
                    style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid var(--outline-variant)', color: 'var(--on-surface)', fontFamily: "var(--font-display)", fontSize: 14, padding: '6px 0', outline: 'none', boxSizing: 'border-box' }}
                    onFocus={e => e.target.style.borderBottomColor = 'var(--primary)'}
                    onBlur={e => e.target.style.borderBottomColor = 'var(--outline-variant)'}
                  />
                </div>
              ))}
            </div>

            {/* Section: Lizenz & Sport */}
            <div style={{ height: 1, background: 'linear-gradient(90deg, rgba(59,73,75,0.4) 0%, transparent 100%)', margin: '0.5rem 0 1rem' }} />
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--primary)', textTransform: 'uppercase', marginBottom: 12 }}>
              {t('drivers_form_section_sport').toUpperCase()}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 1.5rem' }}>
              {[
                { label: t('drivers_form_license'), key: 'license_number', type: 'text' },
                { label: t('drivers_form_uci'), key: 'uci_id', type: 'text' },
              ].map(({ label, key, type }) => (
                <div key={key} style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--outline)', textTransform: 'uppercase', marginBottom: 6 }}>{label}</label>
                  <input
                    type={type}
                    value={formData[key]}
                    onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                    style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid var(--outline-variant)', color: 'var(--on-surface)', fontFamily: "var(--font-display)", fontSize: 14, padding: '6px 0', outline: 'none', boxSizing: 'border-box' }}
                    onFocus={e => e.target.style.borderBottomColor = 'var(--primary)'}
                    onBlur={e => e.target.style.borderBottomColor = 'var(--outline-variant)'}
                  />
                </div>
              ))}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--outline)', textTransform: 'uppercase', marginBottom: 6 }}>{t('drivers_form_gender').toUpperCase()}</label>
                <select
                  value={formData.gender}
                  onChange={(e) => {
                    const g = e.target.value;
                    const cats = getCategoriesForGender(g || null);
                    const currentCatValid = cats.includes(formData.category);
                    setFormData({ ...formData, gender: g, category: currentCatValid ? formData.category : cats[0] });
                  }}
                  style={{ width: '100%', background: 'var(--surface)', border: 'none', borderBottom: '1px solid var(--outline-variant)', color: 'var(--on-surface)', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, padding: '6px 0', outline: 'none' }}
                >
                  <option value="">—</option>
                  <option value="männlich">{t('drivers_form_gender_m')}</option>
                  <option value="weiblich">{t('drivers_form_gender_f')}</option>
                </select>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--outline)', textTransform: 'uppercase', marginBottom: 6 }}>{t('drivers_form_class').toUpperCase()}</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  style={{ width: '100%', background: 'var(--surface)', border: 'none', borderBottom: '1px solid var(--outline-variant)', color: 'var(--on-surface)', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, padding: '6px 0', outline: 'none' }}
                >
                  {getCategoriesForGender(formData.gender || null).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--outline)', textTransform: 'uppercase', marginBottom: 6 }}>{t('drivers_form_points').toUpperCase()}</label>
                <input
                  type="number"
                  value={formData.points}
                  onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 0 })}
                  min="0"
                  style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid var(--outline-variant)', color: 'var(--on-surface)', fontFamily: "'JetBrains Mono', monospace", fontSize: 14, padding: '6px 0', outline: 'none', boxSizing: 'border-box' }}
                  onFocus={e => e.target.style.borderBottomColor = 'var(--primary)'}
                  onBlur={e => e.target.style.borderBottomColor = 'var(--outline-variant)'}
                />
              </div>
            </div>
            {isAdminOrCoAdmin && (
              <div style={{ marginTop: '0.5rem', paddingTop: '1rem', borderTop: '1px solid rgba(59,73,75,0.2)' }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--primary)', textTransform: 'uppercase', marginBottom: 12 }}>{t('drivers_form_section_login').toUpperCase()}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 1.5rem' }}>
                  {[
                    { label: `${t('drivers_form_email')} *`, key: 'account_email', type: 'email' },
                    { label: editingId ? t('drivers_form_pw_optional') : t('drivers_form_password'), key: 'account_password', type: 'password' },
                  ].map(({ label, key, type }) => (
                    <div key={key} style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--outline)', textTransform: 'uppercase', marginBottom: 6 }}>{label}</label>
                      <input
                        type={type}
                        value={accountData[key]}
                        onChange={(e) => setAccountData({ ...accountData, [key]: e.target.value })}
                        style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid var(--outline-variant)', color: 'var(--on-surface)', fontFamily: "var(--font-display)", fontSize: 14, padding: '6px 0', outline: 'none', boxSizing: 'border-box' }}
                        onFocus={e => e.target.style.borderBottomColor = 'var(--primary)'}
                        onBlur={e => e.target.style.borderBottomColor = 'var(--outline-variant)'}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
              <button type="submit" style={{ padding: '10px 20px', background: 'var(--primary)', color: '#002022', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', border: 'none', cursor: 'pointer' }}>
                {editingId ? t('drivers_save').toUpperCase() : t('drivers_add').toUpperCase()}
              </button>
              <button type="button" onClick={resetForm} style={{ padding: '10px 20px', background: 'transparent', border: '1px solid var(--outline-variant)', color: 'var(--on-surface-variant)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', cursor: 'pointer' }}>
                {t('drivers_cancel').toUpperCase()}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── RIDER TABLE ── */}
      {loading ? (
        <div style={{ padding: '3rem', textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--outline)', letterSpacing: '0.15em' }}>
          LOADING ROSTER…
        </div>
      ) : (
        <div style={{ marginBottom: '2rem', border: '1px solid rgba(59,73,75,0.35)', borderRadius: 4, overflow: 'hidden' }}>
          {/* Table header */}
          <div className="driver-table-row driver-table-header">
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', textTransform: 'uppercase' }}>RANG</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--primary)', textTransform: 'uppercase' }}>{t('drivers_col_pts')}</span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', textTransform: 'uppercase' }}>{t('drivers_col_rider')}</span>
            <span className="driver-col-id" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', textTransform: 'uppercase' }}>{t('drivers_col_id')}</span>
            <span className="driver-col-class" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', textTransform: 'uppercase' }}>{t('drivers_col_class')}</span>
            <span />
          </div>

          {!ownerVisible && visibleDrivers.length === 0 ? (
            <div style={{ padding: '2.5rem', textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--outline)', letterSpacing: '0.12em' }}>
              {t('drivers_not_found')}
            </div>
          ) : (
            <>
              {/* ── OWNER ROW (always first) ── */}
              {ownerVisible && (() => {
                const initials = (ownerRow.name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
                const ownerCat = ownerRow.category || null;
                const ownerCs = ownerCat ? (CATEGORY_STYLE[ownerCat] || CATEGORY_STYLE.Elite) : null;
                return (
                  <div
                    key="owner"
                    onClick={() => {
                      if (isAdmin) navigate('/owner-profile');
                      else if (ownerRow.owner_driver_id) navigate(`/drivers/${ownerRow.owner_driver_id}`);
                    }}
                    className="driver-table-row"
                    style={{ borderTop: 'none', background: 'rgba(var(--brand-primary-rgb),0.04)', cursor: (isAdmin || ownerRow.owner_driver_id) ? 'pointer' : 'default', transition: 'background 0.12s', borderBottom: '2px solid rgba(var(--brand-primary-rgb),0.15)' }}
                    onMouseEnter={e => { if (isAdmin || ownerRow.owner_driver_id) e.currentTarget.style.background = 'rgba(var(--brand-primary-rgb),0.09)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(var(--brand-primary-rgb),0.04)'; }}
                  >
                    {/* Col 1: Rang */}
                    {ownerRow.is_racing && ownerRow.radnet_rang
                      ? <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 700, color: 'var(--on-surface)', lineHeight: 1 }}>{ownerRow.radnet_rang}</span>
                        </div>
                      : <span />
                    }
                    {/* Col 2: Points — only when owner is racing */}
                    {ownerRow.is_racing
                      ? <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 16, fontWeight: 700, color: 'var(--primary)', lineHeight: 1 }}>{ownerRow.points ?? 0}</span>
                        </div>
                      : <span />
                    }
                    {/* Col 2: Avatar + Name + badges */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <div style={{ width: 34, height: 34, flexShrink: 0, borderRadius: '50%', background: ownerRow.photo_url ? 'transparent' : 'rgba(var(--brand-primary-rgb),0.12)', border: '2px solid rgba(var(--brand-primary-rgb),0.4)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {ownerRow.photo_url
                          ? <img src={ownerRow.photo_url} alt={ownerRow.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <span style={{ fontFamily: "var(--font-display)", fontSize: 12, fontWeight: 800, fontStyle: 'italic', color: 'var(--primary)' }}>{initials}</span>
                        }
                      </div>
                      <div className="drv-name-badges" style={{ minWidth: 0 }}>
                        <span className="drv-name" style={{ fontFamily: "var(--font-display)", fontSize: 13, fontWeight: 600, color: 'var(--primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                          {ownerRow.name}
                        </span>
                        {ownerRow.is_racing && ownerCs && (
                          <span className="drv-mobile-cat" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', color: ownerCs.color, background: ownerCs.bg, border: `1px solid ${ownerCs.border}`, padding: '2px 6px', borderRadius: 99, whiteSpace: 'nowrap' }}>
                            {ownerCat.toUpperCase()}
                          </span>
                        )}
                        <div className="drv-badges" style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 7, fontWeight: 700, letterSpacing: '0.12em', background: 'rgba(var(--brand-primary-rgb),0.15)', color: 'var(--primary)', border: '1px solid rgba(var(--brand-primary-rgb),0.4)', padding: '1px 5px', flexShrink: 0 }}>{t('drivers_badge_owner')}</span>
                          {!ownerRow.is_racing && (
                            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 7, fontWeight: 700, letterSpacing: '0.1em', background: 'rgba(132,148,149,0.12)', color: '#849495', border: '1px solid rgba(132,148,149,0.3)', padding: '1px 5px', flexShrink: 0 }}>NOT RACING</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Col 3: ID */}
                    <span className="driver-col-id" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 500, color: 'var(--outline)', letterSpacing: '0.05em' }}>
                      {ownerRow.public_id || '—'}
                    </span>
                    {/* Col 4: Category — only shown when owner is racing (hidden on mobile, shown inline in name cell) */}
                    <span className="driver-col-class">
                      {ownerRow.is_racing && ownerCs && (
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', color: ownerCs.color, background: ownerCs.bg, border: `1px solid ${ownerCs.border}`, padding: '3px 7px', borderRadius: 99, whiteSpace: 'nowrap' }}>
                          {ownerCat.toUpperCase()}
                        </span>
                      )}
                    </span>
                    {/* Col 5: Chevron */}
                    <span style={{ color: 'var(--outline)', fontSize: 16, textAlign: 'right' }}>{isAdmin ? '›' : ''}</span>
                  </div>
                );
              })()}
              {/* ── DRIVER ROWS (incl. racing owner, sorted by points) ── */}
              {visibleDrivers.map((driver, i) => {
                const isOwnerEntry = !!driver._isOwner;
                const cat = driver.category || 'Elite';
                const cs = CATEGORY_STYLE[cat] || CATEGORY_STYLE.Elite;
                const isOwn = !isAdminOrCoAdmin && driver.id === driverId;
                const isMyCoAdmin = !isAdmin && isCoAdmin && driver.id === driverId;
                const hasBadge = isOwn || isMyCoAdmin || driver.is_co_admin || isOwnerEntry;
                const initials = driver.name
                  ? driver.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
                  : '??';
                const avatarBorder = isOwnerEntry ? '2px solid rgba(var(--brand-primary-rgb),0.4)' : `1px solid ${cs.border}`;
                const avatarBg = isOwnerEntry
                  ? (driver.photo_url ? 'transparent' : 'rgba(var(--brand-primary-rgb),0.12)')
                  : (driver.photo_url ? 'transparent' : cs.bg);
                const avatarRadius = isOwnerEntry ? '50%' : 3;
                const nameColor = (isOwn || isMyCoAdmin || isOwnerEntry) ? 'var(--primary)' : 'var(--on-surface)';
                const rowBg = (isOwn || isMyCoAdmin || isOwnerEntry) ? 'rgba(var(--brand-primary-rgb),0.04)' : 'transparent';

                return (
                  <div
                    key={driver._isOwner ? 'owner-racing' : driver.id}
                    onClick={() => {
                      if (isOwnerEntry) { if (isAdmin) navigate('/owner-profile'); else if (driver.owner_driver_id) navigate(`/drivers/${driver.owner_driver_id}`); }
                      else navigate(isOwn || isMyCoAdmin ? '/profile' : `/drivers/${driver.id}`);
                    }}
                    className="driver-table-row"
                    style={{
                      borderTop: '1px solid rgba(59,73,75,0.2)',
                      background: rowBg,
                      cursor: 'pointer', transition: 'background 0.12s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(var(--brand-primary-rgb),0.07)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = rowBg; }}
                  >
                    {/* Col 1: Rang */}
                    {driver.radnet_rang
                      ? <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 700, color: 'var(--on-surface)', lineHeight: 1 }}>{driver.radnet_rang}</span>
                        </div>
                      : <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'rgba(132,148,149,0.3)' }}>—</span>
                    }
                    {/* Col 2: Points */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 16, fontWeight: 700, color: 'var(--primary)', lineHeight: 1 }}>{driver.points ?? 0}</span>
                    </div>

                    {/* Col 2: Avatar + Name + badges */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      <div style={{ width: 34, height: 34, flexShrink: 0, borderRadius: avatarRadius, background: avatarBg, border: avatarBorder, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {driver.photo_url
                          ? <img src={driver.photo_url} alt={driver.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <span style={{ fontFamily: "var(--font-display)", fontSize: 12, fontWeight: 800, fontStyle: 'italic', color: isOwnerEntry ? 'var(--primary)' : cs.color }}>{initials}</span>
                        }
                      </div>
                      <div className={`drv-name-badges${hasBadge ? ' has-badge' : ''}`} style={{ minWidth: 0 }}>
                        <span className="drv-name" style={{ fontFamily: "var(--font-display)", fontSize: 13, fontWeight: 600, color: nameColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                          {driver.name || '—'}
                        </span>
                        <span className="drv-mobile-cat" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', color: cs.color, background: cs.bg, border: `1px solid ${cs.border}`, padding: '2px 6px', borderRadius: 99, whiteSpace: 'nowrap' }}>
                          {cat.toUpperCase()}
                        </span>
                        {hasBadge && (
                          <div className="drv-badges" style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
                            {isOwnerEntry && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 7, fontWeight: 700, letterSpacing: '0.12em', background: 'rgba(var(--brand-primary-rgb),0.15)', color: 'var(--primary)', border: '1px solid rgba(var(--brand-primary-rgb),0.4)', padding: '1px 5px', flexShrink: 0 }}>{t('drivers_badge_owner')}</span>}
                            {(isOwn || isMyCoAdmin) && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 7, fontWeight: 700, letterSpacing: '0.1em', background: 'rgba(var(--brand-secondary-rgb),0.15)', color: 'var(--brand-secondary)', border: '1px solid rgba(var(--brand-secondary-rgb),0.3)', padding: '1px 5px', flexShrink: 0 }}>ICH</span>}
                            {driver.is_co_admin && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 7, fontWeight: 700, letterSpacing: '0.1em', background: 'rgba(255,154,31,0.12)', color: '#ff9a1f', border: '1px solid rgba(255,154,31,0.35)', padding: '1px 5px', flexShrink: 0 }}>{t('drivers_badge_manager')}</span>}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Col 3: Rider ID */}
                    <span className="driver-col-id" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 500, color: 'var(--outline)', letterSpacing: '0.05em' }}>
                      {driver.public_id || '—'}
                    </span>

                    {/* Col 4: Category badge (hidden on mobile — shown inline in name cell) */}
                    <span className="driver-col-class">
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', color: cs.color, background: cs.bg, border: `1px solid ${cs.border}`, padding: '3px 7px', borderRadius: 99, whiteSpace: 'nowrap' }}>
                        {cat.toUpperCase()}
                      </span>
                    </span>

                    {/* Col 5: Chevron */}
                    <span style={{ color: 'var(--outline)', fontSize: 16, textAlign: 'right' }}>›</span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* ── MOBILE FABs (Admin/Co-Admin) ── */}
      {isAdminOrCoAdmin && (
        <div className="drv-mobile-fabs" style={{ display: 'none' }}>
          {/* Einladen FAB */}
          <button
            className="drv-mobile-fab-invite"
            onClick={handleOpenInvite}
            style={{ position: 'fixed', bottom: 172, right: 16, width: 48, height: 48, background: 'rgba(var(--brand-secondary-rgb),0.15)', color: 'var(--brand-secondary)', border: '1px solid rgba(var(--brand-secondary-rgb),0.5)', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, boxShadow: '0 0 14px rgba(var(--brand-secondary-rgb),0.25)', zIndex: 40 }}
            title={t('drivers_invite')}
          >
            ✉
          </button>
          {/* NEU FAB */}
          <button
            className="drv-mobile-fab-new"
            onClick={() => { resetForm(); setShowForm(true); setShowInvite(false); }}
            style={{ position: 'fixed', bottom: 112, right: 16, width: 56, height: 56, background: 'var(--brand-primary)', color: '#002022', border: 'none', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 300, boxShadow: '0 0 20px rgba(var(--brand-primary-rgb),0.4)', zIndex: 40 }}
            title={t('drivers_add')}
          >
            +
          </button>
        </div>
      )}

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }

        /* Name + badges: inline on desktop */
        .drv-name-badges {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 6px;
          min-width: 0;
        }
        .drv-name-badges .drv-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .drv-name-badges .drv-badges { display: flex; flex-direction: row; gap: 4px; flex-wrap: wrap; margin-top: 0; }

        /* ── Mobile overrides (≤ 768px) ── */
        @media (max-width: 768px) {
          /* Badges below name on mobile */
          .drv-name-badges {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 2px !important;
          }
          .drv-name-badges .drv-badges { margin-top: 1px; }

          .drv-mobile-fabs { display: block !important; }
          .drv-mobile-fab-invite { display: flex !important; }
          .drv-mobile-fab-new { display: flex !important; }
          /* Hide admin buttons in header on mobile — FABs take over */
          .drv-header-admin-btns { display: none !important; }

          /* Page header: stack vertically */
          .drv-page-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 10px !important;
          }
          .drv-header-right {
            width: 100%;
            justify-content: space-between;
          }

          /* Invite panel: less padding */
          .drv-invite-panel {
            padding: 1rem !important;
          }
          /* Invite panel header: stack close button below */
          .drv-invite-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 10px !important;
          }
          .drv-invite-close {
            align-self: flex-end;
          }
          /* Mobile: hide ID column, 4-col layout */
          .drv-invite-id-col { display: none !important; }
          .drv-invite-table-header,
          .drv-invite-row {
            grid-template-columns: 30px 1fr 56px 40px !important;
            padding: 7px 10px !important;
            gap: 8px !important;
          }

          /* Join requests: stack vertically, full-width action buttons */
          .drv-jr-row {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 10px !important;
            padding: 12px !important;
          }
          .drv-jr-actions {
            width: 100%;
            display: flex !important;
            gap: 8px !important;
          }
          .drv-jr-actions button {
            flex: 1;
          }

        }
      `}</style>
    </div>
  );
}

export default Drivers;
